(() => {
  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  if (!customElements.get('vallario-purchase-panel')) {
    customElements.define('vallario-purchase-panel', class extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.onChange = () => this.update();
        this.addEventListener('change', this.onChange);
        this.productInfo?.addEventListener('input', this.onChange);
        this.productInfo?.addEventListener('change', this.onChange);
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.productInfo?.removeEventListener('input', this.onChange);
        this.productInfo?.removeEventListener('change', this.onChange);
      }

      readData() {
        try { return JSON.parse(this.querySelector('[data-purchase-data]')?.textContent || '{}'); }
        catch { return {}; }
      }

      restorePlan(id) {
        const data = this.readData();
        const plan = data.plans?.find((item) => item.id === id);
        const repeat = Boolean(plan) || data.required;
        const unavailable = this.querySelector('[data-plan-unavailable]');
        if (unavailable) unavailable.hidden = !id || Boolean(plan);
        if (id && !plan) this.hidden = false;
        const radio = this.querySelector(`input[type="radio"][value="${repeat ? 'repeat' : 'once'}"]`);
        if (radio) radio.checked = true;
        const checkbox = this.querySelector('[data-subscription-opt-in]');
        if (checkbox) checkbox.checked = repeat;
        const select = this.querySelector('[data-plan-select]');
        if (plan && select) select.value = plan.id;
        this.update();
      }

      update() {
        const data = this.readData();
        const quantityInput = this.productInfo?.querySelector('input[name="quantity"]');
        const quantity = Number(quantityInput?.value || 1);
        const validQuantity = Number.isInteger(quantity) && quantity > 0 && (!quantityInput || quantityInput.validity.valid);
        const selectedKind = [...this.querySelectorAll('input[type="radio"]')].find((input) => input.checked);
        const checkbox = this.querySelector('[data-subscription-opt-in]');
        const repeat = Boolean(data.required) || (checkbox ? checkbox.checked : selectedKind?.value === 'repeat');
        const select = this.querySelector('[data-plan-select]');
        const plan = repeat ? data.plans?.find((item) => item.id === select?.value) : null;
        const hiddenInput = this.querySelector('[data-selling-plan]');
        const fields = this.querySelector('[data-plan-fields]');
        if (fields) fields.hidden = !repeat;
        if (hiddenInput) {
          hiddenInput.disabled = !plan;
          hiddenInput.value = plan?.id || '';
        }
        if (select) select.disabled = !repeat;
        if (select) select.required = repeat;
        const terms = this.querySelector('[data-plan-description]');
        // The merchant-authored selling-plan description is Shopify's source of truth.
        const description = plan?.description || '';
        if (terms && terms.innerHTML !== description) terms.innerHTML = description;
        let unitPrice = plan?.price ?? data.price;
        if (!plan) {
          for (const priceBreak of data.breaks || []) {
            if (quantity >= priceBreak.minimum) unitPrice = priceBreak.price;
          }
        }
        const title = data.variantTitle === 'Default Title' ? '' : (data.variantTitle || '');
        const selection = [title, `× ${quantity}`, plan?.name || this.dataset.oneTimeLabel].filter(Boolean).join(' · ');
        const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency', currency: this.dataset.currency || 'AUD'
        });
        this.productInfo?.querySelectorAll('[data-size-card-price]').forEach((price) => {
          let allocations = [];
          try { allocations = JSON.parse(price.dataset.planPrices || '[]'); } catch {}
          const allocation = plan ? allocations.find((item) => item.id === plan.id) : null;
          const amount = allocation?.price ?? Number(price.dataset.basePrice);
          if (Number.isFinite(amount)) setText(price, formatter.format(amount / 100));
        });
        const valid = validQuantity && Number.isFinite(unitPrice) && (!repeat || Boolean(plan));
        setText(this.querySelector('[data-purchase-selection]'), validQuantity ? selection : this.dataset.unavailable);
        setText(this.querySelector('[data-purchase-total]'), valid ? formatter.format(unitPrice * quantity / 100) : '—');
        this.dataset.purchaseTotal = valid ? formatter.format(unitPrice * quantity / 100) : '';
        const mainPrice = this.productInfo?.querySelector('.vpa-price-block .price-item--regular');
        if (valid && mainPrice) setText(mainPrice, formatter.format(unitPrice / 100));
        const buttonLabel = this.productInfo?.querySelector('.product-form__submit span:not(.svg-wrapper)');
        if (valid && buttonLabel && this.productInfo.dataset.showCtaPrice === 'true' && !this.productInfo.querySelector('.product-form__submit')?.disabled) {
          setText(buttonLabel, `${this.productInfo.dataset.ctaLabel || 'Add to bag'} · ${this.dataset.purchaseTotal}`);
        }
        const charge = this.querySelector('[data-checkout-charge]');
        if (charge) {
          charge.hidden = !valid || !plan || plan.checkoutCharge === unitPrice;
          if (!charge.hidden) setText(charge, `Due at checkout: ${formatter.format(plan.checkoutCharge * quantity / 100)}`);
        }
        this.dispatchEvent(new CustomEvent('vallario:purchase-update', { bubbles: true }));
      }
    });
  }

  if (!customElements.get('vallario-mobile-purchase')) {
    customElements.define('vallario-mobile-purchase', class extends HTMLElement {
      connectedCallback() {
        this.productInfo = document.getElementById(`MainProduct-${this.dataset.section}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
        this.form = document.getElementById(this.dataset.formId);
        this.button = this.querySelector('[data-sticky-submit]');
        if (!this.productInfo || !this.mainButton || !this.form || !this.button) return;
        this.refresh = () => {
          if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
        };
        this.onClick = () => {
          if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') return;
          if (!this.form.checkValidity()) {
            this.productInfo.scrollIntoView({ block: 'start', behavior: 'smooth' });
            this.form.reportValidity();
            return;
          }
          this.form.requestSubmit(this.mainButton);
        };
        this.button.addEventListener('click', this.onClick);
        window.addEventListener('scroll', this.refresh, { passive: true });
        window.addEventListener('resize', this.refresh);
        document.addEventListener('focusin', this.refresh);
        document.addEventListener('focusout', this.refresh);
        this.productInfo.addEventListener('vallario:purchase-update', this.refresh);
        this.observer = new MutationObserver(this.refresh);
        this.observer.observe(this.productInfo, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
        this.cartObserver = new MutationObserver(this.refresh);
        document.querySelectorAll('cart-drawer, cart-notification').forEach((cart) => {
          this.cartObserver.observe(cart, { subtree: true, attributes: true, attributeFilter: ['class', 'open'] });
        });
        this.update();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onClick);
        window.removeEventListener('scroll', this.refresh);
        window.removeEventListener('resize', this.refresh);
        document.removeEventListener('focusin', this.refresh);
        document.removeEventListener('focusout', this.refresh);
        this.productInfo?.removeEventListener('vallario:purchase-update', this.refresh);
        this.observer?.disconnect();
        this.cartObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      update() {
        const footer = document.querySelector('.section-vallario-footer, .footer');
        const cartOpen = document.querySelector('cart-drawer.active, cart-notification.active, .cart-notification.active');
        const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), select, textarea, [contenteditable="true"]');
        const mainButtonBounds = this.mainButton.getBoundingClientRect();
        const mainButtonVisible = mainButtonBounds.top >= 0 && mainButtonBounds.bottom <= window.innerHeight;
        const showAtThisPosition = this.dataset.showBefore === 'true'
          ? !mainButtonVisible
          : mainButtonBounds.bottom < 0;
        const atFooter = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        this.hidden = !mobile || !showAtThisPosition || Boolean(atFooter || cartOpen || editing);
        const busy = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.disabled = busy;
        const label = this.mainButton.querySelector('span:not(.svg-wrapper)')?.textContent.trim() || this.mainButton.textContent.trim();
        setText(this.button, this.productInfo.dataset.ctaLabel || 'Add to bag');
        this.button.setAttribute('aria-label', label);
        const panel = this.productInfo.querySelector('vallario-purchase-panel');
        let variant;
        try { variant = JSON.parse(this.productInfo.querySelector('[data-selected-variant]')?.textContent || '{}'); } catch { variant = {}; }
        const quantity = Number(this.productInfo.querySelector('input[name="quantity"]')?.value || 1);
        const fallbackSelection = [variant.title === 'Default Title' ? '' : variant.title, `${this.dataset.quantityLabel || 'Qty'} ${quantity}`].filter(Boolean).join(' · ');
        setText(this.querySelector('[data-sticky-selection]'), fallbackSelection);
        this.querySelector('[data-sticky-selection]')?.setAttribute('title', panel?.querySelector('[data-purchase-selection]')?.textContent.trim() || fallbackSelection);
        const total = panel?.querySelector('[data-purchase-total]')?.textContent.trim();
        const price = this.productInfo.querySelector('.price:not(.hidden) .price-item--last, .price:not(.hidden) .price-item--regular')?.textContent.trim();
        const currency = this.productInfo.querySelector('variant-selects')?.dataset.currencyCode || 'AUD';
        const fallbackTotal = Number.isFinite(variant.price) && Number.isInteger(quantity) && quantity > 0
          ? new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style:'currency', currency }).format(variant.price * quantity / 100)
          : price;
        setText(this.querySelector('[data-sticky-total]'), total || fallbackTotal || '');
      }
    });
  }
})();
